import React from "react";
import Image from "components/common/Image";
import Badge from "components/common/Badge";
import Icon from "components/common/Icon";
import Link from "components/common/Link";
import Show from "components/common/Show";
import PromotionStatusBadge from "components/common/PromotionStatusBadge";
import { colors } from "gstyles";
import renderPropsComposer from "utils/renderPropsComposer";
import cx from "classnames";
import { get, isArray } from "utils/lodash";
import { formatHour24, formatDate } from "utils/dateFormatter";
import { formatPrice } from "utils/currencyFormatter";
import { DAY_OF_WEEK_MAP_REVERSE } from "utils/dayjs";
import { useModal } from "hooks/useModal";
import { useRouter } from "next/navigation";
interface PromotionProps {
  image: string;
  title: string;
  price: number;
  originalPrice?: number;
  discount?: number;
  discountType?: "percentage" | "new_price";
  discountValue?: number;
  dayOfWeek?: string[];
  slug: string;
  type?: "default" | "golden";
  promotionStatus?: string;
}

const Slot = ({
  id,
  startTime,
  endTime,
  startDate,
  endDate,
  dayOfWeek,
  discountType,
  discountValue,
  promotionStatus,
  status,
  onStatusChange,
  isUpdating,
  ...props
}: any) => {
  const modal = useModal();
  const router = useRouter();
  let valueStartTime = "",
    valueEndTime = "";
  let valueStartDate = "",
    valueEndDate = "";

  if (startTime) {
    valueStartTime = formatHour24(startTime);
  }
  if (endTime) {
    valueEndTime = formatHour24(endTime);
  }

  if (startDate) {
    valueStartDate = formatDate(startDate);
  }
  if (endDate) {
    valueEndDate = formatDate(endDate);
  }

  const handleStatusChange = (newStatus: string) => {
    if (!onStatusChange || !id) return;

    // Define confirmation messages based on status transition
    const confirmMessages: Record<string, string> = {
      available: "Bạn có chắc chắn muốn kích hoạt ưu đãi này?",
      waiting: "Bạn có chắc chắn muốn tạm dừng ưu đãi này?",
      blocked: "Bạn có chắc chắn muốn chặn ưu đãi này?"
    };

    // Special message for reactivating from blocked status
    const message = status === "blocked" && newStatus === "available" 
      ? "Bạn có chắc chắn muốn kích hoạt lại ưu đãi này?"
      : confirmMessages[newStatus];

    modal.confirm({
      title: "Xác nhận",
      content: message,
      okText: "Xác nhận",
      cancelText: "Hủy",
      centered: true,
      onOk: async () => {
        await onStatusChange(id, newStatus);
      }
    });
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Check if the click is on an action button
    const target = e.target as HTMLElement;
    const isButton = target.tagName === 'BUTTON' || target.closest('button');

    // If not clicking a button, navigate to detail page
    if (!isButton && id) {
      router.push(`/promotion/${id}`);
    }
  };

  return (
    <div
      className="relative w-full bg-white overflow-hidden border border-primary/20 rounded-2xl p-4 cursor-pointer hover:shadow-lg transition-shadow"
      onClick={handleCardClick}
    >
      {promotionStatus && (
        <div className="mb-3">
          <PromotionStatusBadge status={promotionStatus} size="sm" />
        </div>
      )}
      <div className="relative flex flex-col gap-2">
        <span className="border-l-2 border-primary pl-2 font-semibold">
          {valueStartTime} - {valueEndTime}
        </span>
        <span className="">
          Thứ:{" "}
          <span className="font-semibold">
            {isArray(dayOfWeek)
              ? dayOfWeek
                  ?.map(
                    (item: string) =>
                      DAY_OF_WEEK_MAP_REVERSE[
                        item as keyof typeof DAY_OF_WEEK_MAP_REVERSE
                      ]
                  )
                  .join(", ")
              : dayOfWeek}
          </span>
        </span>
        <span className="">
          Thời gian:{" "}
          <span className="font-semibold">
            {valueStartDate} - {valueEndDate}
          </span>
        </span>
        <Show.When isTrue={discountType === "percentage"}>
          <span className="">
            Phần trăm khuyến mãi:{" "}
            <span className="font-semibold">{discountValue}%</span>
          </span>
        </Show.When>
      </div>

      {/* Action buttons based on status */}
      {onStatusChange && status && (
        <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
          {status === "waiting" && (
            <>
              <button
                onClick={() => handleStatusChange("available")}
                disabled={isUpdating}
                className="flex-1 bg-primary text-white px-3 py-1.5 rounded-md text-sm font-medium hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdating ? "Đang xử lý..." : "Kích hoạt"}
              </button>
              <button
                onClick={() => handleStatusChange("blocked")}
                disabled={isUpdating}
                className="flex-1 border border-gray-300 text-gray-700 px-3 py-1.5 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdating ? "Đang xử lý..." : "Chặn"}
              </button>
            </>
          )}
          {status === "available" && (
            <>
              <button
                onClick={() => handleStatusChange("waiting")}
                disabled={isUpdating}
                className="flex-1 bg-processing text-white px-3 py-1.5 rounded-md text-sm font-medium hover:bg-processing/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdating ? "Đang xử lý..." : "Tạm dừng"}
              </button>
              <button
                onClick={() => handleStatusChange("blocked")}
                disabled={isUpdating}
                className="flex-1 border border-gray-300 text-gray-700 px-3 py-1.5 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdating ? "Đang xử lý..." : "Chặn"}
              </button>
            </>
          )}
          {status === "blocked" && (
            <button
              onClick={() => handleStatusChange("available")}
              disabled={isUpdating}
              className="w-full bg-primary text-white px-3 py-1.5 rounded-md text-sm font-medium hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUpdating ? "Đang xử lý..." : "Kích hoạt lại"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

function Promotion({
  id,
  name,
  displayName,
  displayDiscountPrice,
  discountType,
  discountValue,
  service,
  shop,
  type,
  promotionStatus,
}: any) {
  const [imageLoaded, setImageLoaded] = React.useState(false);

  // Get image URL from shop avatar
  const imageUrl = get(shop, "avatar.url", "");
  const shopName = get(shop, "name", "Shop");

  return (
    <Link href={`/promotion/${id}`}>
      <div className="relative w-full bg-white overflow-hidden border border-primary/20 rounded-2xl">
        <div className="relative w-full aspect-[2/1] bg-primary/5 overflow-hidden">
          {imageUrl ? (
            <Image
              wrapClassName="absolute inset-0 w-full h-full"
              className={cx("w-full h-full object-cover transition-opacity duration-300", {
                "opacity-0": !imageLoaded,
                "opacity-100": imageLoaded,
              })}
              src={imageUrl}
              alt={`${shopName} image`}
              onLoad={() => setImageLoaded(true)}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/10 to-primary/20">
              <Icon name="image-outline" size={48} color={colors.primary} />
            </div>
          )}
          {imageUrl && !imageLoaded && (
            <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-primary/20 via-primary/10 to-primary/20" />
          )}

          {/* Status Badge - Top Left Corner */}
          {promotionStatus && (
            <div className="absolute top-2 left-2 z-10">
              <PromotionStatusBadge status={promotionStatus} size="sm" />
            </div>
          )}

          {/* Discount Badge - Top Right Corner */}
          <Show.When isTrue={discountType === "percentage"}>
            <Badge label={`-${discountValue}%`} type="discount" />
          </Show.When>
          <Show.When isTrue={discountType === "new_price"}>
            <Badge label={`-${formatPrice(discountValue)}`} type="discount" />
          </Show.When>

          {/* <button className="absolute top-2 right-2 p-1">
          <MoreHorizontal className="w-5 h-5 text-gray-600" />
        </button> */}
        </div>

        <div className="p-4">
          <h3 className="text-base font-medium text-primary-dark line-clamp-2 mb-1">
            {name || displayName || ""}
          </h3>
          <div className="text-black-01 text-sm mb-1">
            {get(shop, "displayedAddress")}
          </div>
          <div className="flex items-end gap-2">
            <div className="flex items-center gap-1">
              <Show>
                <Show.When isTrue={type === "golden"}>
                  <Icon name="crown-outline" size={20} color={colors.primary} />
                </Show.When>
                <Show.Else>
                  <Icon
                    name="sun-fog-outline"
                    size={20}
                    color={colors.primary}
                  />
                </Show.Else>
              </Show>
              <span
                className={cx("text-base font-medium -mb-1", {
                  ["text-primary"]: type !== "golden",
                  ["text-bad"]: type === "golden",
                })}
              >
                {formatPrice(displayDiscountPrice)}
              </span>
            </div>
            <span className="text-primary/50 text-xs line-through">
              {formatPrice(service?.menuPrice)}
            </span>
          </div>
        </div>
      </div>
      <Show.When isTrue={type === "golden"}>
        <div className="flex gap-[0.375rem] w-full py-[0.375rem]">
          <div className="text-black-01 font-base font-semibold bg-processing bg-opacity-20 border-processing border border-opacity-30 py-1 text-center text-sm px-6 rounded-2xl w-1/3">
            Gói 10 lần
          </div>
          <div className="text-black-01 font-base font-semibold bg-processing bg-opacity-20 border-processing border border-opacity-30 py-1 text-center text-sm px-8 rounded-2xl flex-1">
            Có 3 khách đang sử dụng
          </div>
        </div>
      </Show.When>
    </Link>
  );
}

const TypePromotion = ({ typeCard, ...props }: any) => {
  return renderPropsComposer(
    {
      matcher: (typeCard: string) => {
        return typeCard === "slot";
      },
      render: () => {
        return <Slot {...props} />;
      },
    },
    {
      matcher: "default",
      render: () => {
        return <Promotion {...props} />;
      },
    }
  )(typeCard);
};

export default TypePromotion;
